// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { updateClinicPackage } from "../../api";

export const useUpdateClinicPackage = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (payload: {
      packageId: string;
      packageData: { name: string; content: string };
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateClinicPackage(
          token,
          clinicId,
          payload.packageId,
          payload.packageData
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate clinic packages query cache
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clinic-packages", clinicId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Package updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Package could not be updated!"),
      });
    },
  });
};
